import React, { Suspense, lazy, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { useTranslation } from 'react-i18next';
import * as smartDataAnalystService from './smartDataAnalystService';

const PRIMARY = '#009587';
const PRIMARY_DARK = '#006157';
const PANEL_W = 460;
const PANEL_W_EXPANDED = 900;
const ASSISTANT_SOURCE = 'smart-data-analyst';
const OVERLAY_SELECTOR = [
  'dialog[open]',
  '[role="dialog"]',
  '[role="alertdialog"]',
  '[aria-modal="true"]',
  '.mb-mantine-Modal-root',
  '.mb-mantine-Overlay-root',
  '.mb-mantine-Popover-dropdown',
  '.mb-mantine-Menu-dropdown',
  '.mb-mantine-Drawer-root',
  '.ModalContainer'
].join(',');

function isDisplayed(node) {
  if (node.getAttribute('aria-hidden') === 'true' || node.hasAttribute('hidden')) {
    return false;
  }
  const style = window.getComputedStyle(node);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

function isAnalystOverlayOpen(event) {
  if (Array.from(document.querySelectorAll(OVERLAY_SELECTOR)).some(isDisplayed)) {
    return true;
  }
  if (!event || typeof event.composedPath !== 'function') {
    return false;
  }
  return event.composedPath().some((node) => node instanceof Element && node.matches(OVERLAY_SELECTOR) && isDisplayed(node));
}

const SmartDataAnalystSession = lazy(() => import('./SmartDataAnalystSession'));

export default function SmartDataAnalyst({ rightOffset = 24, embedded = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(embedded);
  const [expanded, setExpanded] = useState(false);
  const [openedOnce, setOpenedOnce] = useState(embedded);
  const [instanceUrl, setInstanceUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requestKey, setRequestKey] = useState(0);

  useEffect(() => {
    const onAssistantOpen = (event) => {
      if (event.detail !== ASSISTANT_SOURCE) {
        setOpen(false);
        setExpanded(false);
      }
    };
    window.addEventListener('fp-assistant-open', onAssistantOpen);
    return () => window.removeEventListener('fp-assistant-open', onAssistantOpen);
  }, []);

  useEffect(() => {
    if (!embedded) {
      return undefined;
    }
    const onMessage = (event) => {
      if (!event.data) {
        return;
      }
      if (event.data.type === 'fp-sda-collapse') {
        setExpanded(false);
      } else if (event.data.type === 'fp-sda-expand') {
        setExpanded(true);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [embedded]);

  useEffect(() => {
    const host = document.getElementById('metabase-portal-relocation-host');
    if (host) {
      host.hidden = !open;
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setExpanded(false);
      return undefined;
    }
    setOpenedOnce(true);
    window.dispatchEvent(new CustomEvent('fp-assistant-open', { detail: ASSISTANT_SOURCE }));
    const onKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || isAnalystOverlayOpen(event)) {
        return;
      }
      if (embedded) {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({ type: 'fp-sda-close' }, '*');
        }
        return;
      }
      setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, embedded]);

  useEffect(() => {
    if (!open || instanceUrl) {
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    smartDataAnalystService
      .getConfig()
      .then((response) => {
        if (cancelled) {
          return;
        }
        const url = response.data && response.data.metabaseInstanceUrl;
        if (!url) {
          setError(t('smart_data_analyst_error'));
          return;
        }
        setInstanceUrl(url);
      })
      .catch(() => {
        if (!cancelled) {
          setError(t('smart_data_analyst_error'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, instanceUrl, requestKey, t]);

  const toggle = () => {
    setOpen((current) => !current);
  };

  const closePanel = () => {
    if (embedded) {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: 'fp-sda-close' }, '*');
      }
      return;
    }
    setOpen(false);
  };

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    if (embedded && window.parent && window.parent !== window) {
      window.parent.postMessage({ type: next ? 'fp-sda-expand' : 'fp-sda-collapse' }, '*');
    }
  };

  const retry = () => {
    setError('');
    setInstanceUrl('');
    setRequestKey((key) => key + 1);
  };

  return (
    <>
      {!embedded && (
      <Fab
        aria-label={open ? t('smart_data_analyst_close') : t('smart_data_analyst_open')}
        onClick={toggle}
        sx={{
          position: 'fixed',
          right: rightOffset,
          bottom: 24,
          zIndex: 1200,
          width: 56,
          height: 56,
          padding: 0,
          minHeight: 56,
          overflow: 'hidden',
          backgroundColor: PRIMARY,
          color: '#fff',
          boxShadow: '0 6px 18px rgba(0,101,94,.35)',
          transition: 'transform .2s ease, box-shadow .2s ease',
          '&:hover': {
            backgroundColor: PRIMARY_DARK,
            transform: 'scale(1.06)',
            boxShadow: '0 8px 24px rgba(0,101,94,.45)'
          }
        }}
      >
        {open ? (
          <CloseIcon />
        ) : (
          <Box
            component="img"
            src="/img/smart-data-analyst-logo.svg"
            alt=""
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block',
              transform: 'scale(0.78)'
            }}
          />
        )}
      </Fab>
      )}

      {openedOnce && (
        <Box
          role="complementary"
          aria-label={t('smart_data_analyst')}
          aria-hidden={!open}
          sx={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            left: embedded ? 0 : 'auto',
            width: embedded ? '100%' : `${expanded ? PANEL_W_EXPANDED : PANEL_W}px`,
            maxWidth: '100vw',
            height: '100vh',
            flexDirection: 'column',
            border: 'none',
            overflow: 'hidden',
            backgroundColor: '#fff',
            zIndex: 1300,
            boxShadow: embedded ? 'none' : '0 12px 40px rgba(0,0,0,.18)',
            transition: 'width .3s ease',
            display: open ? 'flex' : 'none'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexShrink: 0,
              alignItems: 'center',
              gap: 1.5,
              height: 64,
              px: 2,
              borderBottom: '1px solid #e6eeed',
              backgroundColor: '#fff'
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: PRIMARY,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0
              }}
            >
              <Box
                component="img"
                src="/img/smart-data-analyst-logo.svg"
                alt=""
                sx={{ width: '78%', height: '78%', objectFit: 'contain', display: 'block' }}
              />
            </Box>
            <Typography
              component="h2"
              sx={{
                flex: 1,
                m: 0,
                fontSize: 16,
                fontWeight: 600,
                lineHeight: '40px',
                color: '#16302e'
              }}
            >
              {t('smart_data_analyst')}
            </Typography>
            <IconButton
              aria-label={expanded ? t('smart_data_analyst_collapse') : t('smart_data_analyst_expand')}
              onClick={toggleExpanded}
              sx={{ color: '#5c6b6a', '&:hover': { backgroundColor: 'rgba(0,149,135,0.08)' } }}
            >
              {expanded ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
            </IconButton>
            <IconButton
              aria-label={t('smart_data_analyst_close')}
              onClick={closePanel}
              sx={{ color: '#5c6b6a', '&:hover': { backgroundColor: 'rgba(0,149,135,0.08)' } }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ minHeight: 0, flex: 1, display: 'flex', overflow: 'hidden' }}>
            {error && (
              <Box sx={{ m: 'auto', px: 3, textAlign: 'center' }}>
                <Typography color="text.secondary" variant="body2">
                  {error}
                </Typography>
                <IconButton aria-label={t('smart_data_analyst_open')} onClick={retry} sx={{ mt: 1 }}>
                  <AutoAwesomeIcon />
                </IconButton>
              </Box>
            )}
            {!error && (loading || !instanceUrl) && (
              <Box sx={{ m: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={32} sx={{ color: PRIMARY }} />
                <Typography color="text.secondary" variant="body2">
                  {t('smart_data_analyst_loading')}
                </Typography>
              </Box>
            )}
            {!error && instanceUrl && (
              <Suspense
                fallback={
                  <Box sx={{ m: 'auto' }}>
                    <CircularProgress size={32} sx={{ color: PRIMARY }} />
                  </Box>
                }
              >
                <SmartDataAnalystSession instanceUrl={instanceUrl} open={open} />
              </Suspense>
            )}
          </Box>
        </Box>
      )}
    </>
  );
}
